import { parseInline, type HighlightColour } from '@/lib/content/tokens'

import { PunkHighlight } from './punk-highlight'

type HighlightedTextProps = {
  text: string
  highlightColour?: HighlightColour
  highlightRotation?: string
  highlightVariant?: 'stripe' | 'marker'
}

/**
 * Synchronous version of RichText for client components. Renders **word**
 * and **word|colour** highlight markup but ignores jargon tokens (since
 * jargon lookup needs a server-side DB call). For server components that
 * need jargon support, use RichText instead.
 */
export function HighlightedText({
  text,
  highlightColour = 'purple',
  highlightRotation = '-1.5deg',
  highlightVariant = 'stripe',
}: HighlightedTextProps) {
  if (!text) return null

  const segments = parseInline(text)
  if (segments.length === 0) return <>{text}</>
  if (segments.length === 1 && segments[0].type === 'text') return <>{segments[0].text}</>

  let key = 0
  const nodes = segments.map((segment) => {
    if (segment.type === 'text') return segment.text

    if (segment.type === 'highlight') {
      return (
        <PunkHighlight
          key={`hl-${key++}`}
          colour={segment.colour ?? highlightColour}
          rotation={highlightRotation}
          variant={highlightVariant}
        >
          {segment.text}
        </PunkHighlight>
      )
    }

    if (segment.type === 'aside') {
      return (
        <span key={`as-${key++}`} className="rkn-aside">
          ({segment.text})
        </span>
      )
    }

    return segment.label ?? segment.slug
  })

  return <>{nodes}</>
}
